import React from 'react';
import { shape } from 'prop-types';
import map from 'lodash/fp/map';

import ProductDisplay, {
  productDisplayPropTypes,
} from 'common/components/shop/ProductDisplay';
import { IntlContextProvider } from 'common/components/Intl';
import ShopButton from 'common/components/ShopButton';
import AddToCartButton from 'apps/cart/wrapped/AddToCartButton';
import SubscriptionCommitButton from 'apps/cart/wrapped/SubscriptionCommitButton';
import {
  getOfferingPrice,
  sortProductOfferingsByPrice,
} from '../../util/offerings';

// For each offering we receive, we need to customize which
// CTA button is shown, and pass it the correct props, some
// of which we receive from Rails.
function getOfferingCTAData(offering, ctaProps) {
  if (offering.commitmentReward) {
    return {
      component: SubscriptionCommitButton,
      props: {
        ...ctaProps,
        buttonComponent: ShopButton,
        buttonProps: { color: 'auto-ship' },
      },
    };
  }

  if (offering.subscriberExclusive) {
    return {
      component: AddToCartButton,
      props: { ...ctaProps, buttonComponent: ShopButton },
    };
  }

  return {
    component: AddToCartButton,
    props: {
      buttonComponent: ShopButton,
      offeringId: offering.id,
      sku: offering.variantSku,
      useQuantityInput: false,
    },
  };
}

function CTAButtonList({ product, ctaPropsByOfferingId }) {
  const sortedOfferings = sortProductOfferingsByPrice(product);

  // We want to communicate the price of each offering on the
  // CTA button if there are multiple offerings.
  const showPrice = sortedOfferings.length > 1;

  return map(offering => {
    const { component: Component, props } = getOfferingCTAData(
      offering,
      ctaPropsByOfferingId[offering.id]
    );

    return (
      <Component
        key={offering.id}
        {...props}
        price={showPrice ? getOfferingPrice(product, offering) : undefined}
      />
    );
  })(sortedOfferings);
}

export default function ProductDisplayContainer({
  intlContext,
  productDisplayProps,
  ctaPropsByOfferingId,
}) {
  return (
    <IntlContextProvider {...intlContext}>
      <ProductDisplay
        renderCallToActionButtons={product => (
          <CTAButtonList
            product={product}
            ctaPropsByOfferingId={ctaPropsByOfferingId}
          />
        )}
        {...productDisplayProps}
      />
    </IntlContextProvider>
  );
}

ProductDisplayContainer.propTypes = {
  intlContext: shape().isRequired,
  productDisplayProps: shape(productDisplayPropTypes).isRequired,
  ctaPropsByOfferingId: shape(),
};
